import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { useLocation, useNavigation, useNavigationType } from 'react-router';
import { DirectoryEmptyResults } from './DirectoryEmptyResults';
import { cn } from '@rocco/utils/cn';
import type {
    TileViewModel,
    TileableModel,
} from '@rocco/ui/tile-shared/view-models/tile';
import { Grid } from '@rocco/ui/layout/components/Grid';
import { CategoryPageTileWithAdminActions } from './CategoryPageTileWithAdminActions';
import { directoryTileImageSizes } from './directory-tile-image-sizes';
import { useInfiniteScrollSentinel } from './hooks/useInfiniteScrollSentinel';
import { PRODUCTS_PER_PAGE } from './hooks/useProductViewMore';
import { ScrollReveal } from '~/ui-rocco/pages/home-page/components/ScrollReveal';
import { parseURL as parseCanonicalURL } from '@archipro-website/top-navigation';
import { useIsBelowLg } from '~/ui-rocco/hooks/use-is-below-lg';
import { useIsBelowMd } from '~/ui-rocco/hooks/use-is-below-md';
import {
    useViewportPageSync,
    viewportPageMarkerProps,
    VIEWPORT_PAGE_MARKER_ATTR,
} from '~/ui-rocco/hooks/use-viewport-page-sync';
import type { DirectoryGridInjection } from '~/ui-rocco/pages/directory-browse/use-directory-grid-injections';
import { ContentGatePanel } from '~/modules-rocco/content-gate/ContentGatePanel';
import type { ContentGateState } from '~/modules-rocco/content-gate/content-gate';
import { useDirectoryBrowseTheme } from '~/ui-rocco/pages/directory-browse/directory-browse-theme';
import { LoadMoreButton } from '~/ui-rocco/pages/detail-shared/LoadMoreButton';
import { useGetMatchedPromptImpression } from '~/modules-rocco/get-matched/use-prompt-impression';
import { useOpenGetMatchedModal } from '~/state/ui-state-get-matched-modal';

/** Desktop column counts the grid can render (the Display dropdown's range). */
export type DirectoryGridColumns = 2 | 3 | 4 | 5 | 6;

/** Column gap style — fixed per vertical, never varies with the column count. */
export type DirectoryGridGapVariant = 'standard' | 'projects' | 'imageLabel';

const DEFAULT_INJECT_EVERY_ROWS = 6;

// The uniform browse page is a fixed 36-cell budget (BROWSE_PRODUCTS_PER_PAGE
// — the products a page fetches and the cells it spends them on). Cells are
// products, except these 1-based offsets within every 36-cell page, which
// take an amplifier (AAA) tile whenever the caller supplies any, cycling
// through the provided tiles. Amplifier cells spend cells from the budget
// rather than adding to it: the products they displace open the next page, so
// a category with no amplified professionals renders the same 36-cell page,
// just all products. 36 divides evenly by 2, 3, 4 and 6 columns; at 5 it does
// not (36 % 5 = 1), which is why rowAlignedProductCount trims the tail to a
// whole row rather than relying on the page size to come out even. The
// offsets mix parity and residues so the AAA cell drifts across columns at
// every column count (an even step would pin it to one column at 2-up). At the
// six-row cadence no amplifier cell ever lands on a module-injection boundary:
// boundaries are multiples of 12, 18, 24, 30 or 36 depending on the column
// count, and none of those is congruent to 8, 21 or 34 mod 36 (for 30 that is
// unsolvable outright — gcd(30, 36) = 6 divides none of them). The boundary
// check below still runs after amplifier cells, so a future cadence that does
// collide stays correct rather than silently skipping a module.
const AMPLIFIER_PAGE_CELLS = 36;
const AMPLIFIER_CELL_OFFSETS = [8, 21, 34];

/** Whether the 1-based grid cell is one of the reserved amplifier offsets. */
const isAmplifierCell = (cell: number): boolean =>
    AMPLIFIER_CELL_OFFSETS.includes(((cell - 1) % AMPLIFIER_PAGE_CELLS) + 1);

/** How many of the first `cells` grid cells are amplifier cells. */
const amplifierCellsWithin = (
    cells: number,
    hasAmplifiers: boolean,
): number => {
    if (cells <= 0 || !hasAmplifiers) {
        return 0;
    }
    return (
        Math.floor(cells / AMPLIFIER_PAGE_CELLS) *
            AMPLIFIER_CELL_OFFSETS.length +
        AMPLIFIER_CELL_OFFSETS.filter(
            (offset) => offset <= cells % AMPLIFIER_PAGE_CELLS,
        ).length
    );
};

/**
 * Total grid cells needed to lay out `products` products, counting the
 * amplifier cells interleaved among them. Each amplifier cell pushes the total
 * out by one, which can itself bring another amplifier offset into range, so
 * this settles rather than solving in one pass — two iterations is already more
 * than the offsets can require.
 */
const cellsForProducts = (products: number, hasAmplifiers: boolean): number => {
    let cells = products;
    for (let pass = 0; pass < 4; pass += 1) {
        const next = products + amplifierCellsWithin(cells, hasAmplifiers);
        if (next === cells) {
            break;
        }
        cells = next;
    }
    return cells;
};

/**
 * How many products to draw so the grid ends on a complete row.
 *
 * The page size divides 2, 3, 4 and 6 columns evenly but not 5, and amplifier
 * cells shift the flow on top of that — so "whole pages loaded" does not mean
 * "whole rows drawn", and at 5 columns the last row before View More was left
 * with a single tile and four gaps. Rounding down to a whole number of rows
 * holds the remainder back until the next page arrives, which is the same
 * bargain infinite scroll already makes elsewhere: a short row is a layout
 * bug, a slightly later tile is not.
 *
 * Only while more products are coming. At the true end of the results a
 * partial row is the honest shape, and holding tiles back there would hide
 * them for good.
 */
const rowAlignedProductCount = (
    loadedProducts: number,
    columns: number,
    hasAmplifiers: boolean,
): number => {
    // Walk down rather than dividing: a trailing amplifier cell never renders
    // (they are inserted ahead of a product, so one with nothing after it is
    // not drawn), which means cell count is not a straight function of product
    // count and rounding the cells then converting back overshoots. Stepping
    // down and asking the layout directly is exact.
    //
    // Two rows of headroom, not one: dropping a product can remove an
    // amplifier cell with it, so the total falls by two and can step straight
    // over the multiple it was looking for. Exhaustively checked over every
    // column count and load size — one row's headroom leaves ragged rows,
    // two clears them, and at most 8 products are ever held back.
    const floor = Math.max(1, loadedProducts - columns * 2);
    for (let products = loadedProducts; products >= floor; products -= 1) {
        if (cellsForProducts(products, hasAmplifiers) % columns === 0) {
            return products;
        }
    }
    // Fewer products than a single row — drawing nothing would be worse than
    // drawing a short one.
    return loadedProducts;
};

// Column classes per desktop column count. Tailwind needs the strings literal,
// so each count keeps its own full class string.
// Mobile columns are set per vertical: products stay 2-up (compare-and-shop
// density), while projects and professionals go single column so the imagery
// leads, Instagram-style. Desktop columns come from the page default or the
// user's Display selection. 5 and 6 columns only apply from lg — between md
// and lg the tiles would be under ~120px wide, so those selections clamp to 4.
const COL_CLASSES: Record<DirectoryGridColumns, string> = {
    2: 'grid-cols-2 md:grid-cols-2',
    3: 'grid-cols-2 md:grid-cols-3',
    4: 'grid-cols-2 md:grid-cols-4',
    5: 'grid-cols-2 md:grid-cols-4 lg:grid-cols-5',
    6: 'grid-cols-2 md:grid-cols-4 lg:grid-cols-6',
};
const SINGLE_COL_CLASSES: Record<DirectoryGridColumns, string> = {
    2: 'grid-cols-1 md:grid-cols-2',
    3: 'grid-cols-1 md:grid-cols-3',
    4: 'grid-cols-1 md:grid-cols-4',
    5: 'grid-cols-1 md:grid-cols-4 lg:grid-cols-5',
    6: 'grid-cols-1 md:grid-cols-4 lg:grid-cols-6',
};
// Figma: products/professionals use a 24px desktop column gap; projects use
// 22px. All use a 100px desktop row gap. The gap is keyed by vertical (not
// column count) so changing the Display selection never changes it.
const GAP_CLASSES: Record<DirectoryGridGapVariant, string> = {
    standard: 'gap-x-2 gap-y-12 md:gap-x-3 md:gap-y-[6.25rem]',
    // Projects used a slightly narrower gutter than the rest; at 12px there is
    // nothing left to differ by, so the two variants now share the column gap
    // and keep their own row rhythm.
    projects: 'gap-x-2 gap-y-12 md:gap-x-3 md:gap-y-[6.25rem]',
    // Spaces / Styles browse an image wall rather than a list of projects:
    // uncaptioned tiles on a tight 8px gutter both ways (Figma 5764:115678).
    // Deliberately tighter than the 12px the captioned grids share.
    imageLabel: 'gap-2',
};

/** Default skeleton tile aspect — the portrait tile most verticals use. */
const DEFAULT_SKELETON_ASPECT_CLASS = 'aspect-tile-portrait';

/**
 * Scroll position keyed by URL, restored on Back. `useProductViewMore` restores
 * the exact tiles the user saw (so page height is deterministic — tiles have a
 * fixed portrait aspect), but React Router's native restoration can fire before
 * the cached tiles finish laying out and land short. Saving/restoring here with
 * a double-RAF lands on the exact position instead.
 */
const gridScrollCache = new Map<string, number>();

/** Give up re-asserting a restored scroll position after this long. */
const SCROLL_RESTORE_TIMEOUT_MS = 2500;
/**
 * Keep holding the restored position for this long after first landing on it.
 * Landing once is not enough: a Back into a wider result set streams thousands
 * of rows in *above* the reader over the following frames, and the browser
 * drags the viewport along with them.
 */
const SCROLL_RESTORE_SETTLE_MS = 700;
/** Below this fraction of the viewport showing grid, the reader is stranded. */
const STRANDED_GRID_FRACTION = 1 / 3;

/**
 * Put the reader back on `savedY` and keep them there while the page settles.
 *
 * Two things fight a plain `scrollTo` here. The revalidated tiles are not in the
 * DOM yet, so the page can still be too short to reach `savedY` and the browser
 * clamps the jump. And once they do arrive, scroll anchoring pins the viewport
 * to whatever content it had latched onto and carries it down as rows insert
 * above — which is what turned a Back from a 1-result filter into a landing deep
 * in the footer. So: suspend anchoring for the duration, re-assert every frame
 * until we have held the target for `SCROLL_RESTORE_SETTLE_MS`, and bail the
 * moment the reader scrolls so we never fight them.
 *
 * Returns a cleanup that cancels the loop and restores anchoring.
 */
/**
 * Anchoring is suspended by a refcount, not per-call: two restores can overlap
 * (a fast double-Back re-enters while the first is still holding), and a naive
 * save/restore would have the second capture `none` as the value to put back —
 * leaving anchoring off for the rest of the session.
 */
let anchorSuspensions = 0;
let anchorBeforeSuspend = '';

/** Keys that scroll the page — anything else must not abandon a restore. */
const SCROLL_RESTORE_KEYS = new Set([
    ' ',
    'ArrowDown',
    'ArrowUp',
    'ArrowLeft',
    'ArrowRight',
    'PageDown',
    'PageUp',
    'Home',
    'End',
]);

/**
 * How many restores are currently driving the scroll. The continuous save must
 * stand down while one runs: the restore's own `scrollTo` fires `scroll` events,
 * and until the revalidated page is tall enough those report a CLAMPED position
 * — which would be written over the very depth we are restoring to. Interrupt
 * the restore (deadline, cleanup, reader takes over) and the cache would be left
 * holding the clamped value, so the next Back lands short.
 */
let scrollRestoresInFlight = 0;

const suspendScrollAnchoring = (): (() => void) => {
    const root = document.documentElement;
    if (anchorSuspensions === 0) {
        anchorBeforeSuspend = root.style.overflowAnchor;
        root.style.overflowAnchor = 'none';
    }
    anchorSuspensions++;
    let released = false;
    return () => {
        if (released) return;
        released = true;
        anchorSuspensions--;
        if (anchorSuspensions === 0) {
            root.style.overflowAnchor = anchorBeforeSuspend;
        }
    };
};

const restoreScrollPosition = (savedY: number): (() => void) => {
    const releaseAnchoring = suspendScrollAnchoring();
    // Suppress the continuous save while we drive the scroll ourselves — see
    // SCROLL_RESTORE_KEYS' sibling note on `scrollRestoresInFlight`.
    scrollRestoresInFlight++;

    let frame = 0;
    let landedAt = 0;
    let userTookOver = false;
    const deadline = performance.now() + SCROLL_RESTORE_TIMEOUT_MS;
    const takeOver = () => {
        userTookOver = true;
    };
    // Only keys that actually scroll count as the reader taking over. Tab,
    // Escape and shortcuts must not abandon a restore that has not landed yet.
    const takeOverOnKey = (event: KeyboardEvent) => {
        if (SCROLL_RESTORE_KEYS.has(event.key)) takeOver();
    };

    let cleanedUp = false;
    const cleanup = () => {
        if (cleanedUp) return;
        cleanedUp = true;
        if (frame) cancelAnimationFrame(frame);
        frame = 0;
        window.removeEventListener('wheel', takeOver);
        window.removeEventListener('touchmove', takeOver);
        window.removeEventListener('keydown', takeOverOnKey);
        scrollRestoresInFlight--;
        releaseAnchoring();
    };

    const step = () => {
        frame = 0;
        if (userTookOver) {
            cleanup();
            return;
        }
        window.scrollTo({ top: savedY, behavior: 'instant' });
        const now = performance.now();
        const landed = Math.abs(window.scrollY - savedY) <= 2;
        if (!landed) landedAt = 0;
        else if (!landedAt) landedAt = now;
        const settled =
            landedAt > 0 && now - landedAt >= SCROLL_RESTORE_SETTLE_MS;
        if (settled || now > deadline) {
            cleanup();
            return;
        }
        frame = requestAnimationFrame(step);
    };

    window.addEventListener('wheel', takeOver, { passive: true });
    window.addEventListener('touchmove', takeOver, { passive: true });
    window.addEventListener('keydown', takeOverOnKey);
    step();
    return cleanup;
};

/**
 * The search string without the `page` param (empty string when nothing
 * remains). `?page=N` is rewritten via replaceState as the reader scrolls
 * (useViewportPageSync), so the scroll key must stay stable across it; every
 * other param is a different result set and keys its own scroll position.
 */
const stripPageParam = (search: string): string => {
    const params = new URLSearchParams(search);
    params.delete('page');
    const remaining = params.toString();
    return remaining ? `?${remaining}` : '';
};

interface DirectoryUniformGridProps {
    /** All tiles currently loaded (skeleton + server + View More). */
    productTiles: TileViewModel<TileableModel>[];
    /**
     * Desktop grid columns (2–6) — the page default (4 for products and
     * professionals, 3 for projects) or the user's Display selection.
     */
    columns?: DirectoryGridColumns;
    /** Mobile columns below md. Products keep 2; projects/professionals use 1. */
    mobileColumns?: 1 | 2;
    /** Column gap style. Projects use a slightly narrower desktop column gap. */
    gapVariant?: DirectoryGridGapVariant;
    /**
     * Aspect class for skeleton tiles — must match the vertical's tile aspect
     * so page height stays deterministic for scroll restoration (projects are
     * landscape, everything else portrait).
     */
    skeletonAspectClass?: string;
    /** Plural noun for the end-of-list message (e.g. "products", "projects"). */
    entityNoun?: string;
    /** Tiles per deep-link page (skeleton tile count). Default 42 (products). */
    pageSize?: number;
    /**
     * Fixed `sizes` for the tile media, in place of the column-aware default.
     * The image walls ask for the same candidate the three-column /projects
     * grid does: bigger than their own tiles need, but already in the cache
     * from that page, and it is what the preview opens on.
     */
    imageSizes?: string;
    /** Total results across the catalogue (for the end-of-list message). */
    totalCount: number;
    /** Whether a View More fetch is in progress. */
    isLoading?: boolean;
    /** Whether more products remain to load. */
    hasMore?: boolean;
    /** Trigger loading the next page. */
    onLoadMore?: () => void;
    /** Error message from the last fetch, if any. */
    error?: string | null;
    /**
     * First page with loaded products (1-indexed). Pages below this render as
     * skeletons that lazy-load when scrolled into view (deep-link `?page=N`).
     */
    firstLoadedPage?: number;
    /** The page the user deep-linked to — scrolled to on mount. */
    requestedPage?: number;
    /** Lazy-load callback fired when a skeleton page enters the viewport. */
    onLoadSkeletonPage?: (pageNumber: number) => void;
    /** Pages currently loading (drives skeleton loading state). */
    loadingPages?: Set<number>;
    /** Full-width modules cycled into the grid at the injection cadence. */
    injections?: DirectoryGridInjection[];
    /** Inject a module every N desktop rows (default 3). */
    injectEveryRows?: number;
    /**
     * Pass the first injection boundary by, so the cadence starts at the
     * second. For grids whose tiles are short enough that the first boundary
     * arrives before the reader has seen much of anything.
     */
    skipFirstInjection?: boolean;
    /**
     * Pre-rendered analytics-amplifier (AAA) tiles, cycled through the
     * amplifier cells of each 36-cell page — the uniform-grid equivalent of
     * the legacy grid's per-section amplifier slot. They spend cells out of
     * the page budget, so callers page products in BROWSE_PRODUCTS_PER_PAGE
     * (36) whether or not they have any: the products an amplifier cell
     * displaces open the next page instead of being dropped.
     */
    amplifierTiles?: ReactNode[];
    /**
     * Active category name (e.g. "Building"). When set, the end-of-list message
     * reads "…in {label}" with a Get Matched prompt.
     */
    activeCategoryLabel?: string;
    /**
     * Hybrid load: gate infinite scroll behind an explicit first "Load More"
     * click. Page 1 shows the button with no auto-load; the first click loads
     * page 2 and switches into infinite scroll for the rest of the session.
     * Default false (infinite scroll active from the first batch).
     */
    hybridLoad?: boolean;
    /**
     * Load the next page as the reader reaches the end of this one, without
     * waiting for a first View More click. The button stays on screen as the
     * way through if the sentinel never fires, and as the crawlable `?page=N`
     * anchor.
     */
    autoLoadMore?: boolean;
    /**
     * Projects currently pinned to the top of the directory — drives the
     * admin context menu's Pin/Unpin label on project tiles.
     */
    featuredProjectIds?: number[];
    /**
     * IDs mid Remove-From-Library fade-out. The tile stays mounted at
     * opacity-0 so the animation can play before the caller drops it.
     */
    removedFromLibraryIds?: Set<number>;
    /** Admin action: pin/unpin a project to the top of the directory. */
    onToggleFeature?: (projectId: number, feature: boolean) => void;
    /** Admin action: soft-hide a project/product from the directory. */
    onRemoveFromLibrary?: (
        itemType: 'project' | 'product',
        itemId: number,
        itemName: string,
    ) => void;
    /**
     * Crawlable `?page=N` URL for the Load More control. When set, the control
     * renders as a real `<a href>` (with a JS-intercepted click) so bots can
     * walk the item set without executing JS.
     */
    nextPageHref?: string;
    /**
     * Own `?page=N`: keep the param in step with the page in view via
     * useViewportPageSync. Only for the infinite-scroll browse callers whose
     * View More hooks have their request-time rewrite off (updateUrlOnLoad:
     * false). MUST stay off on instances with real page-based pagination
     * (the suppliers grid) — the sync would fight the pager's ?page.
     */
    syncPageParam?: boolean;
    /** Category for the DirectoryPageChange event syncPageParam logs. */
    trackCategory?: string;
    /**
     * Content gate for this request. When closed the grid ends at the tiles the
     * loader allowed, blurred out under the sign-in panel, and every way onward
     * — View More, the end-of-list message, Back to Top — is replaced by it.
     */
    contentGate?: ContentGateState;
}

/** Placeholder tile shown while a deep-link page lazy-loads. */
const SkeletonTile = ({ aspectClass }: { aspectClass: string }) => {
    const isDark = useDirectoryBrowseTheme() === 'dark';
    const bar = cn(
        'animate-pulse rounded',
        isDark ? 'bg-offwhite/10' : 'bg-gray-100',
    );
    return (
        <div className="flex flex-col gap-3">
            {/* The image placeholder stands in for a tile, so it carries the
                tile's corner; the two below stand in for lines of type. */}
            <div className={cn(aspectClass, 'w-full rounded-tile', bar)} />
            <div className={cn('h-4 w-3/4', bar)} />
            <div className={cn('h-3 w-1/2', bar)} />
        </div>
    );
};

interface SkeletonPageProps {
    pageNumber: number;
    onVisible?: (pageNumber: number) => void;
    colClasses: string;
    gapClasses: string;
    tileCount: number;
    aspectClass: string;
}

/** The `?page=N` an address points at, or undefined without one. Client only. */
const pageOfHref = (href: string | undefined): number | undefined => {
    if (!href) return undefined;
    const url = new URL(href, window.location.origin);
    const page = Number(url.searchParams.get('page'));
    return Number.isInteger(page) && page > 1 ? page : undefined;
};

/**
 * A full page of skeleton tiles for a not-yet-loaded deep-link page. Fires
 * `onVisible` when it approaches the viewport (either scroll direction) so the
 * hook lazy-loads the real tiles for that page.
 */
const DirectoryUniformSkeletonPage = ({
    pageNumber,
    onVisible,
    colClasses,
    gapClasses,
    tileCount,
    aspectClass,
}: SkeletonPageProps) => {
    const ref = useRef<HTMLDivElement>(null);
    const onVisibleRef = useRef(onVisible);
    onVisibleRef.current = onVisible;

    useEffect(() => {
        const node = ref.current;
        if (!node || typeof IntersectionObserver === 'undefined') return;
        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    if (entry.isIntersecting) {
                        onVisibleRef.current?.(pageNumber);
                    }
                }
            },
            { rootMargin: '1500px 0px 1500px 0px', threshold: 0 },
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [pageNumber]);

    return (
        <div
            ref={ref}
            className={pageNumber > 1 ? 'mt-20' : undefined}
            {...viewportPageMarkerProps(pageNumber)}
        >
            <Grid colClasses={colClasses} rowsGapClass={gapClasses}>
                {Array.from({ length: tileCount }).map((_, index) => (
                    <SkeletonTile key={index} aspectClass={aspectClass} />
                ))}
            </Grid>
        </div>
    );
};

/**
 * Uniform portrait product grid for the 5.0 directory browse pages. Renders the
 * same admin/follow-enabled tiles as the legacy category grid (via
 * `CategoryPageTileWithAdminActions`) in a flat responsive grid — no featured
 * A/B sections — matching the Figma browse design.
 *
 * All the paging machinery is owned by `useProductViewMore` in the caller; this
 * component only presents its output: infinite-scroll via the shared sentinel,
 * deep-link skeleton pages (`?page=N`) that lazy-load on scroll, full-width
 * injected modules cycled at a fixed row cadence, and single-cell amplifier
 * (AAA) tiles at reserved offsets within each 36-cell page. POP scroll
 * restoration works because tiles have a fixed portrait aspect, so page
 * height is deterministic.
 */
export const DirectoryUniformGrid = React.memo(
    ({
        productTiles,
        columns = 4,
        mobileColumns = 2,
        gapVariant = 'standard',
        skeletonAspectClass = DEFAULT_SKELETON_ASPECT_CLASS,
        entityNoun = 'products',
        pageSize = PRODUCTS_PER_PAGE,
        imageSizes: imageSizesOverride,
        totalCount,
        isLoading = false,
        hasMore = false,
        onLoadMore,
        error,
        firstLoadedPage = 1,
        requestedPage = 1,
        onLoadSkeletonPage,
        injections,
        injectEveryRows = DEFAULT_INJECT_EVERY_ROWS,
        skipFirstInjection = false,
        amplifierTiles,
        activeCategoryLabel,
        hybridLoad = false,
        autoLoadMore = false,
        nextPageHref,
        syncPageParam = false,
        trackCategory,
        featuredProjectIds,
        removedFromLibraryIds,
        onToggleFeature,
        onRemoveFromLibrary,
        contentGate,
    }: DirectoryUniformGridProps) => {
        const navigationType = useNavigationType();
        const location = useLocation();
        const navigation = useNavigation();
        // End-of-list "Try Get Matched" prompt. Read straight off the shared
        // atom rather than a prop: the browse routes that render this grid all
        // mount the Get Matched modal, and a prop no caller passed left the
        // button inert.
        const openGetMatched = useOpenGetMatchedModal('GridEndPrompt');
        // The line at the foot of the wall always renders; it only carries the
        // Get Matched prompt on a category page, so only then is there a prompt
        // to report as seen.
        const endPromptRef =
            useGetMatchedPromptImpression<HTMLParagraphElement>(
                'GridEndPrompt',
                Boolean(activeCategoryLabel),
            );
        // "Showing all 1 product", not "1 products". Every noun this grid takes
        // (products / projects / professionals / brands) is a regular plural, so
        // dropping the trailing "s" is enough.
        // What the end-of-wall line counts. `totalCount` is the server's row count, which the
        // image wall's collapse makes an overstatement -- it counts documents, not photographs. At
        // the end of the wall the tiles actually loaded are exact, so prefer them, but only when
        // the whole wall was loaded: a reader deep-linked to ?page=5 who scrolls only downward
        // never fetches pages 1-4, and their loaded count would understate badly.
        const wholeWallLoaded = firstLoadedPage === 1;
        const shownCount =
            wholeWallLoaded && productTiles.length > 0
                ? productTiles.length
                : totalCount;
        const countNoun =
            shownCount === 1 ? entityNoun.replace(/s$/, '') : entityNoun;
        // A same-vertical navigation is loading (filter apply, sort change,
        // category switch): the current tiles are about to be replaced. Dim
        // them so the tap reads as acknowledged instead of leaving the stale
        // grid frozen for the loader round trip.
        const isPendingResults =
            navigation.state === 'loading' &&
            navigation.location.pathname.split('/')[1] ===
                location.pathname.split('/')[1];
        const isDark = useDirectoryBrowseTheme() === 'dark';

        const colClasses = (
            mobileColumns === 1 ? SINGLE_COL_CLASSES : COL_CLASSES
        )[columns];
        const gapClasses = GAP_CLASSES[gapVariant];
        // Cadence, stagger and the row trim must all follow the RENDERED
        // column count, not the Display selection, or they land mid-row.
        // COL_CLASSES renders the vertical's fixed mobile count below md, and
        // clamps 5-6 selections to 4 between md and lg; Display persists per
        // entity, so a desktop selection follows the reader to a narrow
        // window and the two diverge there routinely.
        const isBelowMd = useIsBelowMd();
        const isBelowLg = useIsBelowLg();
        const renderedColumns = isBelowMd
            ? mobileColumns
            : isBelowLg && columns >= 5
              ? 4
              : columns;
        // Column-aware image sizes, unless the caller fixed them.
        const tileImageSizes =
            imageSizesOverride ??
            directoryTileImageSizes(columns, mobileColumns);

        // `?page=N` follows the page the reader is actually viewing (page
        // markers on page-start cells + skeleton pages), not the pages the
        // infinite-scroll lookahead has fetched. Opt-in per instance: only
        // the infinite-scroll browse callers own the param — the paginated
        // suppliers instance must never mount this (its pager owns ?page).
        const { commitPage } = useViewportPageSync({
            enabled:
                syncPageParam && (totalCount > pageSize || firstLoadedPage > 1),
            trackCategory,
        });

        // Hybrid load: infinite scroll stays off until the user clicks Load More
        // once. It starts off on every fresh result set — SSR + hydration render
        // the crawlable anchor, not the sentinel — and resets whenever the
        // category / filters / sort change so the new set needs an explicit Load
        // More again. Only meaningful when hybridLoad is set; otherwise infinite
        // scroll is always active (legacy behaviour).
        const [infiniteEnabled, setInfiniteEnabled] = useState(false);
        const infiniteScrollActive =
            autoLoadMore || (hybridLoad ? infiniteEnabled : true);

        // Result-set identity: category + filters + sort as reflected in the URL,
        // with `?page=N` stripped. View More rewrites the page param via
        // replaceState, which does NOT update the router location, so this key is
        // stable through infinite scroll and only changes on a real filter/sort/
        // category (or POP) navigation.
        const filterNavKey = useMemo(() => {
            const params = new URLSearchParams(location.search);
            params.delete('page');
            return `${location.pathname}?${params.toString()}`;
        }, [location.pathname, location.search]);
        useEffect(() => {
            // New result set → gate infinite scroll behind an explicit Load More
            // again. No-op on mount (already false); never fires during infinite
            // scroll (filterNavKey is stable there).
            setInfiniteEnabled(false);
        }, [filterNavKey]);

        // A View More click puts the page it loads in the URL straight away;
        // the infinite-scroll sentinel calls onLoadMore itself, so pages it
        // fetches ahead of the reader still wait for the viewport to reach them.
        const handleLoadMore = useCallback(() => {
            if (hybridLoad) setInfiniteEnabled(true);
            onLoadMore?.();
            const page = pageOfHref(nextPageHref);
            if (page) commitPage(page);
        }, [hybridLoad, onLoadMore, nextPageHref, commitPage]);

        // The Load More control is a real anchor (crawlable). Intercept plain
        // left-clicks to load client-side and switch into infinite mode; let
        // modified clicks (open-in-new-tab etc.) follow the href to the real
        // `?page=N` URL.
        // `HTMLElement`, not `HTMLAnchorElement`: it reads only the modifier
        // keys and the button, and `LoadMoreButton` renders either an anchor
        // or a button depending on whether the next page has an address.
        const handleAnchorClick = useCallback(
            (event: React.MouseEvent<HTMLElement>) => {
                if (
                    event.defaultPrevented ||
                    event.button !== 0 ||
                    event.metaKey ||
                    event.ctrlKey ||
                    event.shiftKey ||
                    event.altKey
                ) {
                    return;
                }
                event.preventDefault();
                handleLoadMore();
            },
            [handleLoadMore],
        );

        // Persist the live scroll position (saved on unmount, i.e. navigating
        // away) so Back can restore it. The key carries the filter/sort params
        // too — an applied filter refreshes the whole result set, so its depth
        // is a different position from the unfiltered page's, and the key
        // change is what drives the scroll-to-top below. Only `?page=N`
        // is stripped: View More rewrites it via replaceState, and the same
        // grid instance must save and restore under one stable key.
        const scrollKey = location.pathname + stripPageParam(location.search);
        const lastScrollYRef = useRef(0);
        // Scroll positions are cached per column count as well as per URL: a
        // saved offset is only meaningful at the layout it was measured in
        // (6-col pages are roughly half the height of 3-col ones). After a
        // Display change, restores for the old count simply miss — landing at
        // the top — instead of re-asserting an unreachable offset for 2.5s.
        const columnsRef = useRef(columns);
        columnsRef.current = columns;
        const scrollCacheKey = `${scrollKey}::c${columns}`;

        // The key the continuous save is *committed* to. It only advances to a
        // new category once the scroll-to-top / POP restore for that navigation
        // has run (see the scrollKey effect below). Between a category PUSH and
        // that scroll adjustment, window.location.pathname already reads the new
        // category while window.scrollY still holds the previous one's depth —
        // saving under the live pathname there would cache the old depth against
        // the new category and Back would restore to the wrong offset.
        const saveKeyRef = useRef(scrollKey);
        // A navigation is in flight (the reader has clicked a filter and the
        // loader is running). Applying a filter closes the accordion / reflows
        // the sidebar, and the resulting scroll adjustment would otherwise
        // overwrite the outgoing page's saved depth just before we leave it.
        const navigatingRef = useRef(false);
        navigatingRef.current = navigation.state !== 'idle';
        useEffect(() => {
            const onScroll = () => {
                lastScrollYRef.current = window.scrollY;
                if (navigatingRef.current || scrollRestoresInFlight > 0) return;
                // Only save once the live URL matches the committed key, so an
                // in-flight (momentum) scroll during a category or filter
                // switch can't mis-attribute the outgoing page's depth to the
                // incoming one.
                const liveKey =
                    window.location.pathname +
                    stripPageParam(window.location.search);
                if (liveKey !== saveKeyRef.current) return;
                gridScrollCache.set(
                    `${saveKeyRef.current}::c${columnsRef.current}`,
                    window.scrollY,
                );
            };
            window.addEventListener('scroll', onScroll, { passive: true });
            return () => window.removeEventListener('scroll', onScroll);
        }, []);

        // Own scroll restoration for this route. The route opts out of React
        // Router's <ScrollRestoration>, which otherwise leaves the browser on
        // `auto` — so on Back, while the loader refetches and the grid is briefly
        // absent, the browser clamps the scroll to the short page's max (a visible
        // jump to a wrong position). Set `manual` while the grid is mounted so it
        // can restore the exact position itself once its tiles are back in the
        // DOM, and restore the previous value on unmount so navigating away
        // doesn't leave the rest of the app on manual scroll restoration.
        useEffect(() => {
            if (typeof window === 'undefined' || !window.history) return;
            const previous = window.history.scrollRestoration;
            window.history.scrollRestoration = 'manual';
            return () => {
                window.history.scrollRestoration = previous;
            };
        }, []);
        // (The unmount-time cache save that lived here is gone: it was racy
        // against the category-change scroll-to-top, which can fire
        // before unmount-ordered cleanups and poison the saved position with
        // 0. The continuous per-scroll save above always holds the latest
        // position under the correct key.)

        // Category identity for the scroll decision below. Filters and sort can
        // live in the pathname as segments (color-pink, sort_trending, and the
        // legacy sort_/company_ forms), so the raw pathname cannot distinguish
        // a category jump from a filter toggle — parse those segments off with
        // the canonical top-navigation parser, which knows the 301-canonical
        // shapes. That parser also strips /suppliers, so fold its flag back in:
        // the Products/Brands toggle is a different page, not a filter.
        const categoryBasePath = useMemo(() => {
            const { basePath, isSuppliersEndpoint } = parseCanonicalURL(
                location.pathname,
            );
            return `${isSuppliersEndpoint ? 'suppliers:' : ''}${basePath}`;
        }, [location.pathname]);

        /** The block wrapping the loaded (non-skeleton) tiles. */
        const loadedGridRef = useRef<HTMLDivElement>(null);

        // The page a paginated grid is on. Only the page-based pagers (brands /
        // suppliers) put `?page=N` in the router location — the infinite-scroll
        // grids rewrite it with a raw `history.replaceState`, which leaves
        // `useLocation` untouched.
        const pageParam = useMemo(
            () => new URLSearchParams(location.search).get('page') ?? '',
            [location.search],
        );

        // Category or filter/sort navigation does NOT remount this grid (same
        // route, same element), so the mount-time restore below never re-runs
        // for it. A category PUSH — or a pager page change — scrolls to the top:
        // the reader asked for a different set and wants to start at the top of
        // it. A filter / sort PUSH holds the current position so they can keep
        // refining without being thrown back up, unless the new (smaller) result
        // set no longer reaches that far. POP restores the saved depth. Skips
        // the first run: mount is the effect below's job (detail-page → Back,
        // deep links).
        const firstKeyRunRef = useRef(true);
        const prevCategoryRef = useRef(categoryBasePath);
        const prevPageParamRef = useRef(pageParam);
        const prevScrollKeyRef = useRef(scrollKey);
        useEffect(() => {
            const categoryChanged =
                prevCategoryRef.current !== categoryBasePath;
            // A pager click moves the page and NOTHING else. When the rest of
            // the key moved too, the page param went along for the ride — a
            // filter applied after a `?page=N` deep link drops it, and that is
            // a filter navigation, not a request for the top of page 1.
            const pagerNavigation =
                prevScrollKeyRef.current === scrollKey &&
                prevPageParamRef.current !== pageParam;
            prevCategoryRef.current = categoryBasePath;
            prevPageParamRef.current = pageParam;
            prevScrollKeyRef.current = scrollKey;
            if (firstKeyRunRef.current) {
                firstKeyRunRef.current = false;
                return;
            }
            // Commit the continuous save to the new key now — before any of the
            // scroll adjustments below — so the onScroll guard suppressed any
            // momentum scroll that fired mid-switch (while the pathname read the
            // new category but the scroll still held the old depth).
            saveKeyRef.current = scrollKey;

            if (navigationType === 'POP') {
                const savedY = gridScrollCache.get(scrollCacheKey);
                return savedY == null
                    ? undefined
                    : restoreScrollPosition(savedY);
            }
            if (categoryChanged || pagerNavigation) {
                window.scrollTo(0, 0);
                return undefined;
            }
            // Filter / sort: hold position — but a filter that collapses the
            // catalogue to a handful of results shortens the page under the
            // reader, and holding then parks them in the footer with the results
            // scrolled off the top (they read it as "the grid is empty"). Once
            // the new set has laid out, pull them back to the grid if it no
            // longer reaches the screen.
            let frame = requestAnimationFrame(() => {
                frame = requestAnimationFrame(() => {
                    const grid = loadedGridRef.current;
                    if (!grid) return;
                    const { top, bottom } = grid.getBoundingClientRect();
                    const stranded =
                        bottom < window.innerHeight * STRANDED_GRID_FRACTION;
                    if (!stranded) return;
                    window.scrollTo({
                        top: Math.max(0, window.scrollY + top),
                        behavior: 'instant',
                    });
                });
            });
            return () => cancelAnimationFrame(frame);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [scrollKey, pageParam]);

        // Skeleton pages to render above the loaded products (deep-link `?page=N`).
        const skeletonPages = useMemo(() => {
            const pages: number[] = [];
            for (let page = 1; page < firstLoadedPage; page++) {
                pages.push(page);
            }
            return pages;
        }, [firstLoadedPage]);

        // On mount, position the scroll once.
        const hasScrolledRef = useRef(false);
        useLayoutEffect(() => {
            if (hasScrolledRef.current) return;

            // Back/Forward (POP): restore the saved scroll position. This keys off
            // gridScrollCache (scroll position), NOT restoredFromCache (tile cache):
            // a Back triggers a loader revalidation, during which the grid streams
            // its tiles in (short page → full height over ~400ms) and can briefly
            // read a viewMoreCache miss — but the saved position is still valid.
            const savedY =
                navigationType === 'POP'
                    ? gridScrollCache.get(scrollCacheKey)
                    : undefined;
            if (savedY != null) {
                hasScrolledRef.current = true;
                return restoreScrollPosition(savedY);
            }

            // Deep-link (`?page=N`) FRESH navigation: no saved scroll position, so
            // scroll to the requested page's marker. The loaded grid's top is NOT
            // the requested page — the loader hydrates pages N-2..N, so the grid
            // starts two pages early for N>=4 and the reader would land there
            // (and the viewport page sync would then honestly rewrite the URL to
            // N-2 on their first gesture). Back navigations never reach here —
            // they carry a saved position (a fresh page load is also 'POP', but
            // has no saved position, so it lands here).
            if (requestedPage > 1 && loadedGridRef.current) {
                hasScrolledRef.current = true;
                let innerRaf = 0;
                const outerRaf = requestAnimationFrame(() => {
                    innerRaf = requestAnimationFrame(() => {
                        const target =
                            document.querySelector(
                                `[${VIEWPORT_PAGE_MARKER_ATTR}="${requestedPage}"]`,
                            ) ?? loadedGridRef.current;
                        target?.scrollIntoView({
                            behavior: 'instant',
                            block: 'start',
                        });
                    });
                });
                return () => {
                    cancelAnimationFrame(outerRaf);
                    cancelAnimationFrame(innerRaf);
                };
            }
            // Run once on mount; inputs are stable for this instance.
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, []);

        // Hybrid mode arms the sentinel only after the reader scrolls: the
        // View More click mounts the sentinel already inside its 2000px
        // lookahead zone, so without this gate the load-completion catch-up
        // fired a second page from the single click (QA: one click loaded two
        // pages). The first scroll flips `enabled`, whose catch-up then loads
        // the next page — which is infinite scroll doing its job.
        const [infiniteArmed, setInfiniteArmed] = useState(false);
        useEffect(() => {
            if (!infiniteEnabled) {
                setInfiniteArmed(false);
                return;
            }
            const arm = () => setInfiniteArmed(true);
            window.addEventListener('scroll', arm, {
                passive: true,
                once: true,
            });
            return () => window.removeEventListener('scroll', arm);
        }, [infiniteEnabled]);

        const sentinelRef = useInfiniteScrollSentinel({
            enabled:
                infiniteScrollActive &&
                (!hybridLoad || infiniteArmed) &&
                hasMore &&
                !isLoading &&
                // A failed page is retried by the hook on its own schedule,
                // then by the reader through the View More control. The
                // sentinel would otherwise re-fire the moment the load
                // settled and hammer a gateway that has just said no.
                !error,
            onTrigger: () => onLoadMore?.(),
        });

        // Interleave the loaded tiles with full-width injected modules and
        // single-cell amplifier (AAA) tiles. The module cadence (every N
        // desktop rows) divides the 36-cell page evenly so an injection lands
        // near each page boundary — the extra height buys time for the next
        // infinite-scroll page to arrive. Positions count every cell
        // (products + amplifiers), so module injections stay on row
        // boundaries as amplifier cells shift the flow.
        const injectEveryTiles = injectEveryRows * renderedColumns;

        // While more products are still to come, draw whole rows only — the
        // remainder is the head of the next row and arrives with it. This is
        // keyed off the live column count rather than the page size, so it
        // holds at every Display setting including the ones the page size does
        // not divide. See rowAlignedProductCount.
        //
        // A closed content gate is the same situation wearing a different hat:
        // the grid stops here, the blur falls across whatever the last row is,
        // and a half-empty one reads as missing content rather than withheld
        // content. The server caps the tiles at a flat count, which can know
        // neither the reader's column selection nor that amplifier cells spend
        // cells of their own, so the row alignment has to happen here. Tiles
        // held back are not lost — signing in re-requests the page ungated.
        const hasAmplifiers = !!amplifierTiles?.length;
        const alignToRows = hasMore || !!contentGate?.gated;
        const visibleTiles = alignToRows
            ? productTiles.slice(
                  0,
                  rowAlignedProductCount(
                      productTiles.length,
                      renderedColumns,
                      hasAmplifiers,
                  ),
              )
            : productTiles;

        const items: ReactNode[] = [];
        let boundaryCount = 0;
        let injectionIndex = 0;
        let amplifierIndex = 0;
        let cellCount = 0;

        // Push the full-width module when the cell just added completed an
        // injection row. Runs after every cell (product or amplifier) so a
        // boundary can never be skipped over. Skips the injection after the
        // very last loaded tile — a boundary injection lands on the last tile
        // of each page, so before Load More (and at the tail during infinite
        // scroll) it would dangle at the end of the grid with no row beneath
        // it, above the button / end message. Once more tiles load it's no
        // longer the last tile and the injection takes its proper
        // between-rows place.
        // Modules are taken in order and put back on the end, so they cycle —
        // except those marked `once`, which are dropped after their first
        // showing and so appear a single time in the whole grid.
        const pool = injections ? [...injections] : [];

        const pushBoundaryInjection = (isLastLoadedTile: boolean) => {
            const atInjectPoint =
                cellCount % injectEveryTiles === 0 && !isLastLoadedTile;
            if (!atInjectPoint) {
                return;
            }
            boundaryCount += 1;
            // The pool is not touched, so nothing is consumed by the boundary
            // that is passed over — the module that would have shown here
            // shows at the next one instead.
            if (skipFirstInjection && boundaryCount === 1) {
                return;
            }
            const injection = pool.shift();
            if (!injection) {
                return;
            }
            if (!injection.once) {
                pool.push(injection);
            }
            // Key by cycle count, not tile index: the tile index shifts
            // when the Display column count changes, and an index-based key
            // would remount every injected module (resetting carousels and
            // re-firing impressions) on a pure layout change.
            const cycleIndex = injectionIndex;
            injectionIndex += 1;
            items.push(
                // Design gives the injected module 160px below it before the
                // next row; the grid row gap already contributes 100px, so add
                // the remaining 60px here.
                //
                // The top is deliberately larger than the bottom rather than
                // matching it. What reads as the module is its statement, and
                // the Get Matched button on the right hangs ~40px below that —
                // so equal padding leaves the statement sitting high in the
                // band. The extra 40px up top puts the same space above the
                // statement as below it, which is what the eye measures.
                <div
                    key={`${injection.key}-${cycleIndex}`}
                    className={cn(
                        'col-span-full md:pt-[5.4rem] md:pb-[3.75rem]',
                        // Below md: 100 either side, of which the grid's 3rem
                        // row gap is already most.
                        'max-md:py-[3.25rem]',
                        // The image wall's 8px row gap leaves the module
                        // crammed against the photos, so it carries the whole
                        // separation itself.
                        gapVariant === 'imageLabel' &&
                            'md:pt-[9.5rem] md:pb-[9.5rem]',
                    )}
                >
                    {injection.node}
                </div>,
            );
        };

        visibleTiles.forEach((tile, index) => {
            const isLastLoadedTile = index === visibleTiles.length - 1;

            // Amplifier (AAA) cells occupy their fixed offsets within each
            // 36-cell page. Inserted ahead of the product heading for that
            // cell, so an amplifier only ever renders with a product after it
            // — it can't dangle at the grid tail while the next page loads.
            while (amplifierTiles?.length && isAmplifierCell(cellCount + 1)) {
                const amplifierTile =
                    amplifierTiles[amplifierIndex % amplifierTiles.length];
                cellCount += 1;
                items.push(
                    <ScrollReveal
                        key={`amplifier-${amplifierIndex}`}
                        // Stretch to the row height; the tile keeps its own
                        // corner radius, matching the grid around it.
                        className="h-full"
                        // No reveal on grid tiles — see the product tile below.
                        immediate
                    >
                        {amplifierTile}
                    </ScrollReveal>,
                );
                amplifierIndex += 1;
                // Load-bearing at 5 columns: the offset-21 cell of every
                // fifth page completes an injection row (see the constants
                // above), so the module must fire here, after the amplifier.
                pushBoundaryInjection(false);
            }

            cellCount += 1;
            const revealTile = (
                <ScrollReveal
                    key={tile.model.id}
                    // Grid tiles don't animate in. A catalogue is something you
                    // scan, and content that fades up under the eye reads as
                    // still loading — the reveal was costing legibility to buy
                    // decoration. `immediate` also returns before the observer
                    // is built, so this drops one IntersectionObserver per tile
                    // rather than merely hiding the transition.
                    //
                    // Unrelated to image loading: the tiles' images carry their
                    // own `loading="lazy"`, which the browser resolves well
                    // before they reach the viewport.
                    immediate
                >
                    <CategoryPageTileWithAdminActions
                        tile={{
                            ...tile,
                            imageSizes: tileImageSizes,
                        }}
                        featuredProjectIds={featuredProjectIds}
                        removedFromLibraryIds={removedFromLibraryIds}
                        onToggleFeature={onToggleFeature}
                        onRemoveFromLibrary={onRemoveFromLibrary}
                    />
                </ScrollReveal>
            );
            // The tile opening each pageSize block of PRODUCTS carries the
            // viewport page marker (`?page=N` follows the marker the reader
            // has scrolled past — see useViewportPageSync). Counted in
            // products, not cells: the server fetches pageSize products per
            // ?page=N (tileCountForPage), while cells also spend amplifier
            // slots — cell-counted markers would drift a full page from the
            // server's offsets every ~12 pages, so a reload of the written
            // URL would show different tiles than the reader was viewing.
            const isPageStartTile = index % pageSize === 0;
            items.push(
                isPageStartTile ? (
                    <div
                        key={tile.model.id}
                        {...viewportPageMarkerProps(
                            firstLoadedPage + index / pageSize,
                        )}
                    >
                        {revealTile}
                    </div>
                ) : (
                    revealTile
                ),
            );
            pushBoundaryInjection(isLastLoadedTile);
        });

        // In hybrid mode the button is only for the first (page N → N+1) step:
        // once infinite scroll takes over it's gone. Non-hybrid keeps it beside
        // the always-active sentinel (legacy behaviour).
        const showLoadMoreButton =
            autoLoadMore || !hybridLoad || !infiniteEnabled;

        const handleScrollToTop = useCallback(() => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        }, []);

        return (
            <div
                className={cn(
                    'px-4 pb-[6.25rem] transition-opacity duration-200 md:px-12',
                    isPendingResults &&
                        'pointer-events-none animate-pulse opacity-40',
                )}
                style={{ overflowAnchor: 'none' }}
            >
                {skeletonPages.map((pageNumber) => (
                    <DirectoryUniformSkeletonPage
                        key={`skeleton-page-${pageNumber}`}
                        pageNumber={pageNumber}
                        onVisible={onLoadSkeletonPage}
                        colClasses={colClasses}
                        gapClasses={gapClasses}
                        // A page spends its whole budget on cells whether or
                        // not amplifier tiles take some of them, so one page
                        // size of skeletons matches the rows it stands in for
                        // and deep-link scroll targets stay put.
                        tileCount={pageSize}
                        aspectClass={skeletonAspectClass}
                    />
                ))}

                {/* Scroll margin clears the sticky pill bar, so a category
                    switch lands on the first row rather than behind the bar.
                    The bar sits lower on mobile, hence the larger inset. */}
                <div
                    ref={loadedGridRef}
                    className="scroll-mt-32 md:scroll-mt-24"
                >
                    <Grid colClasses={colClasses} rowsGapClass={gapClasses}>
                        {items}
                        {/* Two rows of pulse while the next page is in
                            flight, so a reader arriving at the grid's end
                            sees tiles forming rather than the tail sections
                            reading as the end of the listing. The row-aligned
                            grid always ends on a whole row, so these open
                            fresh rows rather than completing a ragged one. */}
                        {isLoading &&
                            hasMore &&
                            Array.from(
                                { length: renderedColumns * 2 },
                                (_, index) => (
                                    <SkeletonTile
                                        key={`loading-row-${index}`}
                                        aspectClass={skeletonAspectClass}
                                    />
                                ),
                            )}
                    </Grid>
                </div>

                {error && (
                    <div
                        className="mt-10 text-center text-base text-red-600"
                        role="alert"
                    >
                        {error}
                    </div>
                )}

                {/* A search or filter combination that matches nothing must say
                    so — an empty grid over the SEO copy reads as a broken page.
                    Skeleton pages mean results are still on their way, so the
                    message only stands once there is genuinely nothing. */}
                {!error &&
                    productTiles.length === 0 &&
                    skeletonPages.length === 0 && (
                        <DirectoryEmptyResults
                            countNoun={countNoun}
                            pathname={location.pathname}
                            search={location.search}
                            isDark={isDark}
                        />
                    )}

                {/* An empty result set has no tail to blur, and no case for a
                    sign-in wall over nothing — the no-results state stands. */}
                {contentGate?.gated && productTiles.length > 0 ? (
                    <ContentGatePanel
                        totalCount={totalCount}
                        visibleTiles={contentGate.visibleTiles}
                        entityNoun={countNoun}
                        behindTiles={contentGate.behindTiles}
                        colClasses={colClasses}
                        gapClasses={gapClasses}
                        tileAspectClass={skeletonAspectClass}
                    />
                ) : hasMore ? (
                    <>
                        {/* Infinite-scroll sentinel: only mounted once infinite
                        mode is active (always active when hybridLoad is off). */}
                        {infiniteScrollActive && (
                            <div
                                ref={sentinelRef}
                                aria-hidden="true"
                                className="h-px w-full"
                            />
                        )}
                        {showLoadMoreButton && onLoadMore && (
                            <LoadMoreButton
                                className="pt-20"
                                label="View More"
                                ariaLabel={`Load more ${entityNoun}`}
                                isLoading={isLoading}
                                // Crawlable where the next page has a real
                                // address; the click still loads in place.
                                href={nextPageHref ?? undefined}
                                onClick={
                                    nextPageHref
                                        ? handleAnchorClick
                                        : handleLoadMore
                                }
                            />
                        )}
                    </>
                ) : (
                    productTiles.length > 0 && (
                        <div className="pt-[6.25rem]">
                            <hr
                                className={cn(
                                    'm-0 border-t',
                                    isDark
                                        ? 'border-offwhite/15'
                                        : 'border-[#141414]/15',
                                )}
                            />
                            <p
                                ref={endPromptRef}
                                className={cn(
                                    'pt-10 text-center text-base',
                                    isDark
                                        ? 'text-offwhite/60'
                                        : 'text-[#767676]',
                                )}
                            >
                                {activeCategoryLabel ? (
                                    <>
                                        Showing all{' '}
                                        {shownCount.toLocaleString()}{' '}
                                        {countNoun} in {activeCategoryLabel}.
                                        Can&rsquo;t find what you&rsquo;re
                                        looking for?{' '}
                                        <button
                                            type="button"
                                            onClick={openGetMatched}
                                            className={cn(
                                                'underline underline-offset-2 hover:no-underline',
                                                isDark
                                                    ? 'text-offwhite'
                                                    : 'text-[#323232]',
                                            )}
                                        >
                                            Try Get Matched
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        Showing all{' '}
                                        {shownCount.toLocaleString()}{' '}
                                        {countNoun}
                                    </>
                                )}
                            </p>
                            <LoadMoreButton
                                className="pt-10"
                                label="Back to Top"
                                ariaLabel="Scroll back to top of page"
                                // The same arrow turned over: this control
                                // goes the other way, it is not a second one.
                                expanded
                                onClick={handleScrollToTop}
                            />
                        </div>
                    )
                )}
            </div>
        );
    },
);

DirectoryUniformGrid.displayName = 'DirectoryUniformGrid';
